/**
 * auth.js - 인증 관련 유틸리티 함수
 * 토큰 유효성 검증 및 만료 시간 확인
 */

/**
 * JWT 토큰 디코딩 (Base64)
 * @param {string} token - JWT 토큰
 * @returns {object|null} 디코딩된 페이로드 또는 null
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    // JWT는 header.payload.signature 형식
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Base64 디코딩 (URL-safe Base64 처리)
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('토큰 디코딩 실패:', error);
    return null;
  }
};

/**
 * 토큰 만료 시간 확인
 * @param {string} token - JWT 토큰
 * @returns {boolean} 토큰이 유효하면 true, 만료되었거나 없으면 false
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  
  // JWT 토큰만 허용 (Mock 토큰 완전 제거)
  const decoded = decodeToken(token);
  if (!decoded) return false;
  
  // exp (expiration time) 클레임 확인
  if (decoded.exp) {
    const currentTime = Math.floor(Date.now() / 1000);
    if (decoded.exp < currentTime) {
      // 토큰이 만료됨
      return false;
    }
  }
  
  // iat (issued at) 클레임이 있으면 발급 시간 확인 (선택적)
  // 토큰이 너무 오래되었는지 확인할 수 있음 (최대 12시간)
  if (decoded.iat) {
    const currentTime = Math.floor(Date.now() / 1000);
    const maxAge = 12 * 60 * 60; // 12시간 (초 단위)
    if (currentTime - decoded.iat > maxAge) {
      return false;
    }
  }
  
  return true;
};

/**
 * 로그인 상태 확인 (토큰 유효성 포함)
 * @returns {boolean} 로그인되어 있고 토큰이 유효하면 true
 */
export const checkAuthStatus = () => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const token = localStorage.getItem('token');
  
  if (!isLoggedIn || !token) {
    return false;
  }
  
  // 토큰 유효성 검증
  if (!isTokenValid(token)) {
    // 토큰이 만료되었거나 유효하지 않으면 로그인 상태 초기화
    clearAuthData();
    return false;
  }
  
  return true;
};

/**
 * 인증 데이터 초기화 (로그아웃)
 */
export const clearAuthData = () => {
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('employeeCode');
  localStorage.removeItem('employeeName');
  localStorage.removeItem('userRole');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('departments');
};

/**
 * 토큰에서 사용자 정보 추출
 * @param {string} token - JWT 토큰
 * @returns {object|null} 사용자 정보 또는 null
 */
export const getUserFromToken = (token) => {
  const decoded = decodeToken(token);
  if (!decoded) return null;
  
  return {
    employee_code: decoded.employee_code || decoded.sub,
    email: decoded.email,
    role: decoded.role,
    exp: decoded.exp,
    iat: decoded.iat
  };
};

